import { UUID } from '../../types/shared/UUID';
import {
  convertIClientOrderDataToIClientOrderResponse,
  convertIClientOrderResponseToIClientOrderData,
  IClientOrderData,
  IClientOrderResponse,
} from '../../types/clientPortal/IClientOrderData';
import { baseUrlService } from '../../ui-toolkit/rest/BaseUrlService';
import {
  convertFromStandardDocumentResponseArrayToStandardDocumentDataArray,
  IStandardDocumentData,
  IStandardDocumentResponse,
} from '../../types/order/documents/shared/interfaces';
import { APIFactory } from '../../ui-toolkit/rest/APIFactory';
import { axiosErrorHandler, axiosErrorHandlerExpects } from '../../ui-toolkit/rest/axiosErrorHandler';
import {
  convertICommentResponseToICommentData,
  ICommentData,
  ICommentResponse,
} from '../../types/order/comment/IComment';
import { ICommentCreateRequest } from '../order/comments/Comments';
import {
  convertFromCancellationStatusResponseToCancellationStatusData,
  IOrderCancellationRequestData,
  IOrderCancellationRequestResponse,
} from '../../ui-toolkit/orders/interfaces/IOrderCancellationRequest';

export const getClientOrderAsync = async (orderId: UUID): Promise<IClientOrderData> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IClientOrderResponse>(`/orders/v5/${orderId}`)
    .then((response) => convertIClientOrderResponseToIClientOrderData(response.data))
    .catch(axiosErrorHandler);

export const getClientDocumentsAsync = async (orderId: UUID): Promise<IStandardDocumentData[]> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IStandardDocumentResponse[]>(`/documents/v5/${orderId}/documents`)
    .then((response) => convertFromStandardDocumentResponseArrayToStandardDocumentDataArray(response.data))
    .catch(axiosErrorHandler);

export const postClientCancelOrderByIDAsync = async ({
  orderId,
  reason,
}: {
  orderId: UUID;
  reason?: string;
}): Promise<IOrderCancellationRequestData> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<IOrderCancellationRequestResponse>(`/orders/v4/${orderId}/cancellation_requests`, { reason })
    .then((response) => convertFromCancellationStatusResponseToCancellationStatusData(response.data))
    .catch(axiosErrorHandlerExpects([404]));

export const getCommentsForClientOrderAsync = async (orderId: string): Promise<ICommentData[]> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<ICommentResponse[]>(`/timber/api/internal/orders/${orderId}/comments?scope=external`)
    .then((result) => result.data.map((it) => convertICommentResponseToICommentData(it)))
    .catch(axiosErrorHandler);

// orderId is needed for Keyed Slice
export const postCommentForClientOrderAsync = async ({
  newComment,
  orderId,
}: {
  newComment: ICommentCreateRequest;
  orderId: UUID;
}): Promise<ICommentData> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<ICommentResponse>(`/timber/api/internal/orders/${orderId}/comments`, newComment)
    .then((result) => convertICommentResponseToICommentData(result.data))
    .catch(axiosErrorHandler);

export const updateClientLoanAmountAsync = async ({
  orderId,
  loanAmount,
}: {
  orderId: UUID;
  loanAmount: number | null;
}): Promise<IClientOrderData> =>
  APIFactory(baseUrlService.ApiLocation)
    .patch(`orders/v5/client/${orderId}/loan_amount`, { loan_amount: loanAmount })
    .then((result) => convertIClientOrderResponseToIClientOrderData(result.data))
    .catch(axiosErrorHandler);

export const updateClientOrderByIdAsync = async ({
  id,
  order,
}: {
  id: string;
  order: IClientOrderData;
}): Promise<IClientOrderData> => {
  let { relocation_employer_name, order_group_id } = convertIClientOrderDataToIClientOrderResponse(order);
  return APIFactory(baseUrlService.ApiLocation)
    .put<IClientOrderResponse>(`/orders/v5/${id}`, {
      relocation_employer_name,
      order_group_id,
    })
    .then((result) => convertIClientOrderResponseToIClientOrderData(result.data))
    .catch(axiosErrorHandler);
};
